import { useState, useCallback, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, ChevronLeft, ChevronRight, Loader2, X, Zap, Sword, Crown, Sparkles, BookOpen } from 'lucide-react'
import { Card } from '../types'
import { useAppStore } from '../store'
import { useCards } from '../hooks/useCards'
import { CardImage, CardImageModal } from '../components/ui/CardImage'
import { api } from '../services/api'

const ITEMS_PER_PAGE = 20

function getRarityColor(rarity: string) {
  const r = rarity?.toLowerCase() || ''
  if (r.includes('cortesano') || r.includes('vasallo') || r.includes('promocional')) return 'bg-green-500/20 text-green-400 border-green-500/30'
  if (r.includes('legendaria')) return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
  if (r.includes('mega') || r.includes('milenaria') || r.includes('ultra')) return 'bg-purple-500/20 text-purple-400 border-purple-500/30'
  if (r.includes('oro') || r.includes('real')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
}

function getFactionColor(faction: string) {
  const colors: Record<string, string> = {
    human: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    demon: 'bg-red-500/20 text-red-400 border-red-500/30',
    elf: 'bg-green-500/20 text-green-400 border-green-500/30',
    dragon: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    undead: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    neutral: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  }
  return colors[faction?.toLowerCase()] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'
}

function getFactionLabel(faction: string) {
  const labels: Record<string, string> = {
    human: 'Humano',
    demon: 'Demonio',
    elf: 'Elfo',
    dragon: 'Dragón',
    undead: 'No Muerto',
    neutral: 'Neutral',
  }
  return labels[faction?.toLowerCase()] || faction
}

const RARITY_OPTIONS = [
  { value: 'ALL', label: 'Todas' },
  { value: 'COMMON', label: 'Comun/Promocional' },
  { value: 'UNCOMMON', label: 'Cortesano/Vasallo' },
  { value: 'RARE', label: 'Oro/Real' },
  { value: 'LEGENDARY', label: 'Legendaria' },
  { value: 'MYTHIC', label: 'Mega/Milenaria/Ultra' },
]

export default function CardBrowserPage() {
  const { cards, isLoading, error, pagination, fetchCards } = useCards()
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [selectedType, setSelectedType] = useState('ALL')
  const [selectedRarity, setSelectedRarity] = useState('ALL')
  const [selectedEdition, setSelectedEdition] = useState('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedCard, setSelectedCard] = useState<Card | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [filterTypes, setFilterTypes] = useState<string[]>([])
  const [filterEditions, setFilterEditions] = useState<string[]>([])
  const [initialized, setInitialized] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const [tilt, setTilt] = useState({ x: 0, y: 0 })

  useEffect(() => {
    api.getTypes().then(setFilterTypes)
    api.getEditions().then(setFilterEditions)
  }, [])

  useEffect(() => {
    const searchParam = searchParams.get('search')
    const editionParam = searchParams.get('edition')
    const pageParam = searchParams.get('page')
    if (searchParam) setSearch(searchParam)
    if (editionParam) setSelectedEdition(editionParam)
    setCurrentPage(pageParam ? parseInt(pageParam) : 1)
    setInitialized(true)
  }, [searchParams])

  useEffect(() => {
    if (selectedCard || showFilters) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [selectedCard, showFilters])

  useEffect(() => {
    if (!initialized) return
    
    const filters: any = { 
      page: currentPage, 
      limit: ITEMS_PER_PAGE,
      search: search || undefined,
      type: selectedType !== 'ALL' ? selectedType : undefined,
      rarity: selectedRarity !== 'ALL' ? selectedRarity : undefined,
      edition: selectedEdition !== 'ALL' ? selectedEdition : undefined
    }
    fetchCards(filters)
    
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (selectedType !== 'ALL') params.set('type', selectedType)
    if (selectedRarity !== 'ALL') params.set('rarity', selectedRarity)
    if (selectedEdition !== 'ALL') params.set('edition', selectedEdition)
    if (currentPage > 1) params.set('page', String(currentPage))
    setSearchParams(params, { replace: true })
  }, [currentPage, search, selectedType, selectedRarity, selectedEdition, initialized])

  const totalPages = pagination.totalPages || Math.ceil(pagination.total / ITEMS_PER_PAGE)

  const clearFilters = useCallback(() => {
    setSearch('')
    setSelectedType('ALL')
    setSelectedRarity('ALL')
    setSelectedEdition('ALL')
    setCurrentPage(1)
    fetchCards({ page: 1, limit: ITEMS_PER_PAGE })
  }, [fetchCards])

  const hasActiveFilters = search !== '' || selectedType !== 'ALL' || selectedRarity !== 'ALL' || selectedEdition !== 'ALL'

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const rotateX = ((y - centerY) / centerY) * -20
    const rotateY = ((x - centerX) / centerX) * 20
    setTilt({ x: rotateX, y: rotateY })
  }, [])

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 })
  }, [])

  return (
    <div className="min-h-screen bg-[#0f1115]">
      <div className="md:hidden fixed bottom-16 left-0 right-0 bg-[#161920] border-t border-[#232730] z-30">
        <div className="px-3 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b919a]" size={18} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setCurrentPage(1) }}}
              placeholder="Buscar..."
              className="w-full pl-10 pr-20 py-2.5 bg-[#1e232a] border border-[#2d3540] rounded-xl text-white placeholder-[#8b919a] focus:outline-none focus:border-[#f59e0b] text-sm"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              {search && <button onClick={() => { setSearch(''); setCurrentPage(1); fetchCards({ page: 1, limit: ITEMS_PER_PAGE })}} className="p-1.5 text-[#8b919a] hover:text-white">×</button>}
              <button onClick={() => setShowFilters(true)} className={`p-1.5 ${hasActiveFilters ? 'text-amber-500' : 'text-[#8b919a] hover:text-white'}`}>
                <Sparkles size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="hidden md:block bg-[#161920] border-b border-[#232730]">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8b919a]" size={20} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setCurrentPage(1) }}}
              placeholder="Buscar cartas..."
              className="w-full pl-12 pr-4 py-3 bg-[#1e232a] border border-[#2d3540] rounded-xl text-white placeholder-[#8b919a] focus:outline-none focus:border-[#f59e0b]"
            />
            {search && (
              <button
                onClick={() => {
                  setSearch('')
                  setCurrentPage(1)
                  fetchCards({ page: 1, limit: ITEMS_PER_PAGE })
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8b919a] hover:text-white"
              >
                ×
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#8b919a] uppercase">Tipo</label>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value)
                  setCurrentPage(1)
                }}
                className="px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white focus:outline-none focus:border-[#f59e0b] cursor-pointer min-w-32"
              >
                <option value="ALL">Todos</option>
                {filterTypes.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-[#8b919a] uppercase">Rareza</label>
              <select
                value={selectedRarity}
                onChange={(e) => {
                  setSelectedRarity(e.target.value)
                  setCurrentPage(1)
                }}
                className="px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white focus:outline-none focus:border-[#f59e0b] cursor-pointer min-w-36"
              >
                {RARITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-xs text-[#8b919a] uppercase">Edición</label>
              <select
                value={selectedEdition}
                onChange={(e) => {
                  setSelectedEdition(e.target.value)
                  setCurrentPage(1)
                }}
                className="px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white focus:outline-none focus:border-[#f59e0b] cursor-pointer min-w-44"
              >
                <option value="ALL">Todas</option>
                {filterEditions.map(edition => (
                  <option key={edition} value={edition}>{edition}</option>
                ))}
              </select>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-sm text-[#f59e0b] hover:text-[#fbbf24] transition-colors"
              >
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowFilters(false)} />
          <div className="relative bg-[#161920] w-full md:max-w-md max-h-[70vh] rounded-t-2xl md:rounded-2xl p-4 md:p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Filtros</h3>
              <button onClick={() => setShowFilters(false)} className="p-2 text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#8b919a] uppercase w-16 shrink-0">Tipo</label>
                <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="flex-1 px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white">
                  <option value="ALL">Todos</option>
                  {filterTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#8b919a] uppercase w-16 shrink-0">Rareza</label>
                <select value={selectedRarity} onChange={(e) => setSelectedRarity(e.target.value)} className="flex-1 px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white">
                  {RARITY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#8b919a] uppercase w-16 shrink-0">Edicion</label>
                <select value={selectedEdition} onChange={(e) => setSelectedEdition(e.target.value)} className="flex-1 px-3 py-2 bg-[#1e232a] border border-[#2d3540] rounded-lg text-sm text-white">
                  <option value="ALL">Todas</option>
                  {filterEditions.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                {hasActiveFilters && <button onClick={() => { clearFilters(); setShowFilters(false) }} className="flex-1 px-4 py-2 text-sm text-[#f59e0b] border border-[#f59e0b] rounded-lg">Limpiar</button>}
                <button onClick={() => { setCurrentPage(1); setShowFilters(false) }} className="flex-1 px-4 py-2 bg-amber-500 text-[#0f1115] font-medium rounded-lg">Aplicar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-8 h-8 text-[#f59e0b] animate-spin" />
            <span className="ml-3 text-[#8b919a]">Cargando cartas...</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-32">
            <p className="text-red-400">Error: {error}</p>
          </div>
        ) : cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32">
            <p className="text-[#8b919a]">No se encontraron cartas</p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 px-4 py-2 bg-[#f59e0b] text-[#0f1115] font-medium rounded-lg hover:bg-[#fbbf24] transition-colors"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 px-2">
              <p className="text-sm text-[#8b919a]">
                Mostrando {cards.length} de {pagination.total} cartas
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 pb-36 md:pb-6">
              {cards.map((card) => (
                <div
                  key={card.id}
                  onClick={() => setSelectedCard(card)}
                  className="cursor-pointer bg-[#1a1e24] rounded-xl overflow-hidden hover:ring-2 hover:ring-[#f59e0b]/50 transition-all"
                >
                  <div className="aspect-[2/3]">
                    <CardImage
                      card={card}
                      className="w-full h-full"
                      showBackAsMain={!card.imageUrl && !(card as Card & { localImage?: string }).localImage}
                    />
                  </div>
                  <div className="pt-2 pb-2 px-2">
                    <h3 className="text-sm font-medium text-white text-center truncate hover:text-[#f59e0b] transition-colors">{card.name}</h3>
                  </div>
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-1 md:gap-2 mt-8 flex-wrap">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 bg-[#1a1e24] border border-[#2d3540] rounded-lg text-[#8b919a] hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft size={16} />
                </button>
                
                {totalPages <= 10 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-[#f59e0b] text-[#0f1115]'
                          : 'bg-[#1a1e24] border border-[#2d3540] text-[#8b919a] hover:text-white'
                      }`}
                    >
                      {page}
                    </button>
                  ))
                ) : (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === 1
                          ? 'bg-[#f59e0b] text-[#0f1115]'
                          : 'bg-[#1a1e24] border border-[#2d3540] text-[#8b919a] hover:text-white'
                      }`}
                    >
                      1
                    </button>
                    
                    {currentPage > 3 && <span className="px-1 text-[#8b919a]">...</span>}
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p > 1 && p < totalPages && Math.abs(p - currentPage) <= 2)
                      .map(page => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-[#f59e0b] text-[#0f1115]'
                              : 'bg-[#1a1e24] border border-[#2d3540] text-[#8b919a] hover:text-white'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    
                    {currentPage < totalPages - 2 && <span className="px-1 text-[#8b919a]">...</span>}
                    
                    <button
                      onClick={() => setCurrentPage(totalPages)}
                      className={`min-w-[36px] h-9 px-2 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === totalPages
                          ? 'bg-[#f59e0b] text-[#0f1115]'
                          : 'bg-[#1a1e24] border border-[#2d3540] text-[#8b919a] hover:text-white'
                      }`}
                    >
                      {totalPages}
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-[#1a1e24] border border-[#2d3540] rounded-lg text-[#8b919a] hover:text-white disabled:opacity-50"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            )}

            <p className="text-center text-sm text-[#8b919a] mt-4">
              {pagination.total} cartas
            </p>
          </>
        )}
      </div>

      {selectedCard && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-3 md:p-4 backdrop-blur-sm"
          onClick={() => setSelectedCard(null)}
        >
          <div 
            className="w-full max-w-[300px] md:max-w-[400px]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedCard(null)}
              className="absolute top-4 right-4 z-10 p-2 bg-[#1a1e24]/80 rounded-full text-white hover:bg-[#2d3540]"
            >
              <X size={24} />
            </button>
            
            <div
              ref={cardRef}
              className="relative cursor-grab active:cursor-grabbing"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onTouchMove={(e) => {
                if (!cardRef.current) return
                const touch = e.touches[0]
                const rect = cardRef.current.getBoundingClientRect()
                const x = touch.clientX - rect.left
                const y = touch.clientY - rect.top
                const centerX = rect.width / 2
                const centerY = rect.height / 2
                const rotateX = ((y - centerY) / centerY) * -20
                const rotateY = ((x - centerX) / centerX) * 20
                setTilt({ x: rotateX, y: rotateY })
              }}
              onTouchEnd={() => setTilt({ x: 0, y: 0 })}
              style={{
                transform: `perspective(1500px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                transition: 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              }}
            >
              <CardImageModal card={selectedCard} />
            </div>

            <div className="mt-4 md:mt-6 p-4 md:p-6 bg-[#1a1e24] rounded-2xl border border-[#2d3540] pb-20 md:pb-6">
              <h2 className="text-xl md:text-2xl font-bold text-white">{selectedCard.name}</h2>
              
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-[#2d3540] rounded text-sm text-white flex items-center gap-1">
                  {selectedCard.type}
                </span>
                <span className={`px-2 py-1 rounded text-sm border ${getRarityColor(selectedCard.rarity)}`}>
                  {selectedCard.rarity}
                </span>
                <span className={`px-2 py-1 rounded text-sm border ${getFactionColor(selectedCard.faction)}`}>
                  <Crown size={12} className="inline mr-1" />
                  {getFactionLabel(selectedCard.faction)}
                </span>
              </div>

              <div className="mt-3 flex gap-4 text-sm">
                <span className="flex items-center gap-1 text-amber-400">
                  <Zap size={16} />
                  Coste: {selectedCard.cost}
                </span>
                {selectedCard.strength && (
                  <span className="flex items-center gap-1 text-red-400">
                    <Sword size={16} />
                    Fuerza: {selectedCard.strength}
                  </span>
                )}
              </div>

              <div className="mt-2 text-xs text-slate-400 flex items-center gap-1">
                <BookOpen size={14} />
                {selectedCard.editionName || selectedCard.edition}
              </div>

              {(selectedCard.effect as any)?.description || selectedCard.effect || selectedCard.skill ? (
                <div className="mt-4 p-3 bg-[#0f1115] rounded-lg">
                  <div className="flex items-center gap-1 text-amber-400 text-xs mb-1">
                    <Sparkles size={14} />
                    Habilidad
                  </div>
                  <p className="text-sm text-[#d1d5db]">
                    {(selectedCard.effect as any)?.description || selectedCard.effect || selectedCard.skill}
                  </p>
                </div>
              ) : null}

              {selectedCard.epicText ? (
                <div className="mt-3 p-3 bg-purple-900/30 rounded-lg border border-purple-500/30">
                  <div className="flex items-center gap-1 text-purple-400 text-xs mb-1">
                    <BookOpen size={14} />
                    Texto Épico
                  </div>
                  <p className="text-sm text-purple-300 italic">
                    {selectedCard.epicText}
                  </p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
